/**
 * Language configuration - single source of truth
 * 
 * To add a new language:
 * 1. Create a new file src/i18n/<lang>.ts exporting a LanguageConfig
 * 2. Import it here and add to the languages object
 * 
 * Everything else (vite config, firebase rewrites, web manifests) is auto-generated!
 */

import type { LanguageConfig } from '@/types'
import { hebrew } from './he'
import { english } from './en'
import { spanish } from './es'
import { portuguese } from './pt'
import { french } from './fr'
import { german } from './de'
import { italian } from './it'
import { russian } from './ru'
import { japanese } from './ja'

// Re-export the LanguageConfig type for external use
export type { LanguageConfig } from '@/types'

export const BASE_URL = 'https://cutmp3.online'

// All supported languages - add new languages here
export const languages: Record<string, LanguageConfig> = {
  he: hebrew,
  en: english,
  es: spanish,
  pt: portuguese,
  fr: french,
  de: german,
  it: italian,
  ru: russian,
  ja: japanese
}

// Default language for x-default hreflang
export const DEFAULT_LANGUAGE = 'en'

// Get all language codes
export const languageCodes = Object.keys(languages) as Array<keyof typeof languages>

// Get canonical URL for a language
export function getCanonicalUrl(langCode: string): string {
  const lang = languages[langCode]
  if (!lang) return BASE_URL
  return lang.path ? `${BASE_URL}${lang.path}/` : `${BASE_URL}/`
}

// Generate hreflang tags for all languages
export function generateHreflangTags(): string {
  const tags: string[] = []
  
  for (const lang of Object.values(languages)) {
    const url = getCanonicalUrl(lang.code)
    tags.push(`<link rel="alternate" hreflang="${lang.code}" href="${url}" />`)
  }
  
  // x-default points to English
  const defaultUrl = getCanonicalUrl(DEFAULT_LANGUAGE)
  tags.push(`<link rel="alternate" hreflang="x-default" href="${defaultUrl}" />`)
  
  return tags.join('\n  ')
}
